import { buildAuthor } from '@components/app/AuthorBox/buildAuthor'
import { NativeAudioPlayer } from '@components/app/Messages/NativeAudioPlayer'
import { NavBar } from '@components/app/NavBar'
import RelatedArticleLinks from '@components/app/RelatedLinkSection/RelatedArticleLinks'
import { RelatedConditionCarousel } from '@components/app/RelatedLinkSection/RelatedConditionCarousel'
import RelatedListingLinks from '@components/app/RelatedLinkSection/RelatedListingLinks'
import RelatedTreatmentTopicLinks from '@components/app/RelatedLinkSection/RelatedTreatmentTopicLinks'
import StructuredData from '@components/app/StructuredData'
import NoIndex from '@components/primitives/Layout/HeaderComponents/NoIndex'
import OpenGraphCard from '@components/primitives/Layout/HeaderComponents/OpenGraphCard'
import type { PageWidth } from '@components/primitives/Layout/HeaderComponents/ResponsivePageMargin'
import TitleForHead from '@components/primitives/Layout/HeaderComponents/TitleForHead'
import { Text } from '@components/primitives/Text'
import { classMerge } from '@components/utilities/classMerge'
import Routes from '@lib/routes'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import ExternalRoutes from '@lib/routes/ExternalRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import shortenMetaDescriptionForGoogle from '@lib/routes/helpers/shortenMetaDescriptionForGoogle'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import toSentence from '@lib/utilities/text/toSentence'
import type { ConjuredArticlePageData } from '@modules/conjuredArticles/helpers/getConjuredArticlePageData'
import type { ConjuredArticleElement } from '@prisma/client'
import Head from 'next/head'
import Image from 'next/image'
import { Suspense } from 'react'
import { jsonLdScriptProps } from 'react-schemaorg'
import type { Article } from 'schema-dts'
import ArticleCitations from './ArticleCitations'
import { ConjuredArticleMarkdown } from './ConjuredArticleMarkdown'
import { ConjuredArticleSearchCard } from './ConjuredArticleSearchCard.client'
import TableOfContents from './TableOfContents.client'
import { headerBlurDataUrl } from './headerImagePlaceholder'

const BLOG_CONTENT_WIDTH = 'max-w-4xl' as PageWidth
const HEADER_IMAGE_HEIGHT = 470
const HEADER_IMAGE_WIDTH = 896

export type ReducedConjuredArticleElement = Pick<
  ConjuredArticleElement,
  | 'heading'
  | 'content'
  | 'id'
  | 'metadata'
  | 'label'
  | 'briefHeading'
  | 'priority'
>

// check if the article is missing briefHeadings
function hasBriefHeadings(elements: ReducedConjuredArticleElement[]) {
  return (
    elements.every((element) => element.briefHeading !== null) &&
    elements.length > 0
  )
}

// Take the heading and content and insert images after the content
function buildMarkdownFromConjuredArticleElement({
  content,
  heading,
  images,
}: {
  content: string
  heading: string
  images: {
    externalImage: { altText: string | null; externalId: string } | null
  }[]
}) {
  const md = [`## ${heading}`, content] // heading and paragraph content
  if (images && images.length > 0) {
    // append images using standard Markdown after the content if there are any to show
    images.map((image) => {
      if (image && image.externalImage && image.externalImage.externalId) {
        // use the imageId as the src for the image, since the <CloudflareImage /> requires it
        md.push(
          `![${image.externalImage?.altText}](${image.externalImage!
            .externalId!})`,
        )
      }
    })
  }
  return md.join(`\n\n`)
}

// We only want to include text elements in the jsonLd article body
function buildArticleBodyFromConjuredArticleElement(
  elements: ReducedConjuredArticleElement[],
) {
  const textElements = elements.filter(
    (element) => element.label !== 'comparison-table',
  )
  return textElements.map((element) => element.content).join('\n\n')
}

function ConjuredArticleGuidePage({
  audioFileUrl,
  authorName,
  citations,
  description,
  elements,
  metadata,
  noindexedAt,
  publishedAt,
  relatedArticles,
  relatedDirectories,
  relatedListings,
  relatedTreatmentTopics,
  relatedTrials,
  slug,
  title,
}: ConjuredArticlePageData) {
  // Split the markdown to insert a carousel halfway through
  const markdownMidpoint = Math.floor(elements.length / 2)
  const markdownFirstHalf = elements
    .slice(0, markdownMidpoint)
    .map(buildMarkdownFromConjuredArticleElement)
    .join(`\n\n`)
  const markdownSecondHalf = elements
    .slice(markdownMidpoint, elements.length)
    .map(buildMarkdownFromConjuredArticleElement)
    .join(`\n\n`)

  const metaDescription =
    shortenMetaDescriptionForGoogle(description ?? elements[0]!.content) ??
    elements[0]!.content

  const authorNameWithFallback = authorName ?? 'Michael Bask Gill'
  const relatedCondition = metadata.relatedCondition
  const isNoIndex = noindexedAt && noindexedAt < new Date()
  const relatedTrialsTitle = `Related trials for ${
    metadata.searchIntentKeywords.length > 0
      ? toSentence(metadata.searchIntentKeywords)
      : 'this article'
  }`

  return (
    <>
      <Head>
        <meta content={metaDescription} name='description' />
      </Head>
      <div className={classMerge('relative px-4 md:px-8')}>
        <OpenGraphCard
          description={metaDescription}
          title={title}
          url={absoluteUrl(DynamicRoutes.guides(slug))}
        />
        <StructuredData
          id={`${slug}-article`}
          scriptTagProps={jsonLdScriptProps<Article>({
            '@context': 'https://schema.org',
            '@type': 'Article',
            'articleBody': buildArticleBodyFromConjuredArticleElement(elements),
            'author': buildAuthor({
              authorName: authorNameWithFallback,
              url: absoluteUrl(Routes.aboutMichaelGill),
            }),
            'datePublished': (publishedAt ?? new Date()).toISOString(),
            'description': metaDescription,
            'headline': title,
          })}
        />
        <TitleForHead title={title} />
        {isNoIndex && <NoIndex />}
        <NavBar maxWidth={BLOG_CONTENT_WIDTH} />
        <div
          className={classMerge(
            'grid grid-cols-1 gap-4',
            BLOG_CONTENT_WIDTH,
            'mx-auto mb-3',
          )}
        >
          <Image
            alt={`Header Image for ${title}`}
            blurDataURL={headerBlurDataUrl}
            className='mt-14 rounded-2xl shadow-lg'
            height={HEADER_IMAGE_HEIGHT}
            loading='lazy'
            placeholder='blur'
            src={ExternalRoutes.merianHeaderImage({
              height: HEADER_IMAGE_HEIGHT,
              title,
              width: HEADER_IMAGE_WIDTH,
            })}
            width={HEADER_IMAGE_WIDTH}
          />
          <Text
            className={'mb-4 mt-12'}
            element='h1'
            styleName={'h2'}
            value={title}
          />
          {audioFileUrl && (
            <div>
              <Text
                styleName='p-strong'
                value='Listen to the article instead of reading through it.'
              />
              <NativeAudioPlayer src={audioFileUrl} />
            </div>
          )}
          {hasBriefHeadings(elements) && (
            <TableOfContents elements={elements} />
          )}
          <ConjuredArticleMarkdown markdown={markdownFirstHalf} />
          {relatedTrials && (
            <RelatedListingLinks
              relatedListings={relatedTrials ?? []}
              title={relatedTrialsTitle}
              trackingSource={TrackingSources.ARTICLE}
            />
          )}
          <Suspense fallback={null}>
            <ConjuredArticleSearchCard topic={relatedCondition} />
          </Suspense>
          <ConjuredArticleMarkdown markdown={markdownSecondHalf} />
          <RelatedListingLinks
            relatedListings={relatedListings ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
          <RelatedConditionCarousel
            relatedDirectories={relatedDirectories ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
          <RelatedArticleLinks
            relatedArticles={relatedArticles ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
          <RelatedTreatmentTopicLinks
            relatedTreatmentTopics={relatedTreatmentTopics ?? []}
            trackingSource={TrackingSources.ARTICLE}
          />
          {citations && <ArticleCitations citations={citations} />}
        </div>
      </div>
    </>
  )
}

export default ConjuredArticleGuidePage
